/**
 * Detalle de cliente — réplica del backoffice de Skeleton.
 *   - Header: avatar + nombre + Active + Email verificado + ID + arrow back
 *   - 4 KPI cards: Total gastado / Ticket promedio / Órdenes totales / Devoluciones
 *   - Información: Email / Teléfono / Registrado / Última orden
 *   - Sección Órdenes (N): lista
 *   - Sección Devoluciones (N): lista o vacío
 */
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Card, Badge, Button } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import {
  ArrowLeft, CheckCircle2, User, Mail, Phone, Calendar, Box, RotateCcw,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/customers/$customerId')({
  component: CustomerDetailPage,
})

interface OrderRow {
  id: string
  orderNumber: string
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded'
  total: number
  currency: string
  createdAt: string
}

interface ReturnRow {
  id: string
  status: string
  refundAmount: number | null
  createdAt: string
}

interface CustomerDetailResponse {
  data: {
    customer: {
      id: string
      email: string
      firstName: string | null
      lastName: string | null
      phone: string | null
      createdAt: string
    }
    orders: OrderRow[]
    returns: ReturnRow[]
  }
}

function CustomerDetailPage() {
  const { id, customerId } = Route.useParams()
  const { activeTenantId } = useAuth()

  const { data, isLoading, error } = useQuery({
    queryKey: ['customer', id, customerId],
    queryFn: () => tenantApi.get<CustomerDetailResponse>(`/api/v1/customers/${customerId}`),
    enabled: !!activeTenantId,
  })

  if (isLoading) return <div className="p-8 text-sm text-neutral-500">Cargando cliente...</div>
  if (error) return <div className="p-8 text-sm text-red-400">Error: {(error as Error).message}</div>
  if (!data?.data) return <div className="p-8 text-sm text-neutral-500">Cliente no encontrado</div>

  const { customer, orders, returns: customerReturns } = data.data
  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ') ||
    (customer.email.split('@')[0] ?? customer.email)
  const initials = ((customer.firstName?.[0] ?? customer.email[0] ?? '?').toUpperCase()) +
    ((customer.lastName?.[0] ?? '').toUpperCase())
  const idShort = customer.id.replace(/-/g, '').slice(0, 8)

  // KPIs derivados
  const paidOrders = orders.filter((o) => o.paymentStatus === 'paid')
  const totalSpent = paidOrders.reduce((s, o) => s + o.total, 0)
  const avgTicket = paidOrders.length > 0 ? Math.round(totalSpent / paidOrders.length) : 0
  const cancelledCount = orders.filter((o) => o.status === 'cancelled').length

  const lastOrderAt = orders.length > 0 ? orders[0]!.createdAt : null
  const currency = orders[0]?.currency ?? 'COP'

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <Link
          to="/store/$id/customers"
          params={{ id }}
          className="p-1.5 mt-1 rounded-md hover:bg-neutral-800 text-neutral-400 hover:text-white transition"
        >
          <ArrowLeft className="size-5" />
        </Link>
        <div className="size-12 rounded-md bg-neutral-800 border border-neutral-700 flex items-center justify-center text-base font-bold uppercase shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold tracking-tight">{fullName}</h1>
            <Badge variant="success">Active</Badge>
            <span className="flex items-center gap-1 text-xs text-emerald-400">
              <CheckCircle2 className="size-3.5" /> Email verificado
            </span>
          </div>
          <p className="text-xs text-neutral-500 font-mono mt-1">ID #{idShort}</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <KpiCard
          label="Total gastado"
          value={formatMoney(totalSpent, currency)}
          hint={`${paidOrders.length} ${paidOrders.length === 1 ? 'orden pagada' : 'órdenes pagadas'}`}
        />
        <KpiCard
          label="Ticket promedio"
          value={formatMoney(avgTicket, currency)}
        />
        <KpiCard
          label="Órdenes totales"
          value={String(orders.length)}
          hint={cancelledCount > 0 ? `${cancelledCount} ${cancelledCount === 1 ? 'cancelada' : 'canceladas'}` : undefined}
          hintColor="text-red-400"
        />
        <KpiCard
          label="Devoluciones"
          value={String(customerReturns.length)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4">
        {/* Información */}
        <Card className="p-5">
          <h3 className="flex items-center gap-2 text-sm font-semibold mb-4">
            <User className="size-4" /> Información
          </h3>
          <div className="space-y-4 text-sm">
            <InfoItem icon={Mail} label="Email" value={customer.email} />
            <InfoItem icon={Phone} label="Teléfono" value={customer.phone ?? '—'} mono />
            <InfoItem
              icon={Calendar}
              label="Registrado"
              value={new Date(customer.createdAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}
            />
            <InfoItem
              icon={Box}
              label="Última orden"
              value={lastOrderAt
                ? new Date(lastOrderAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })
                : '—'}
            />
          </div>
        </Card>

        {/* Órdenes + Devoluciones */}
        <div className="space-y-4">
          {/* Órdenes */}
          <Card className="p-5">
            <h3 className="flex items-center gap-2 text-sm font-semibold mb-4">
              <Box className="size-4" /> Órdenes ({orders.length})
            </h3>
            {orders.length === 0 ? (
              <p className="text-sm text-neutral-500 py-6 text-center">Sin órdenes</p>
            ) : (
              <div className="space-y-2">
                {orders.map((o) => (
                  <Link
                    key={o.id}
                    to="/store/$id/orders/$orderId"
                    params={{ id, orderId: o.id }}
                    className="flex items-center gap-4 px-3 py-2.5 rounded-md hover:bg-neutral-800/40 transition"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm font-bold">{o.orderNumber}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        {new Date(o.createdAt).toLocaleString('es-CO', {
                          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <span className={cn('text-xs font-mono', orderStatusColor(o.status))}>{o.status}</span>
                    <span className={cn('text-xs font-mono', paymentStatusColor(o.paymentStatus))}>{o.paymentStatus}</span>
                    <span className="font-mono font-bold text-sm shrink-0">{formatMoney(o.total, o.currency)}</span>
                  </Link>
                ))}
              </div>
            )}
          </Card>

          {/* Devoluciones */}
          <Card className="p-5">
            <h3 className="flex items-center gap-2 text-sm font-semibold mb-4">
              <RotateCcw className="size-4" /> Devoluciones ({customerReturns.length})
            </h3>
            {customerReturns.length === 0 ? (
              <p className="text-sm text-neutral-500 py-6 text-center">Sin devoluciones</p>
            ) : (
              <div className="space-y-2">
                {customerReturns.map((r) => (
                  <div key={r.id} className="flex items-center gap-4 px-3 py-2.5 rounded-md bg-neutral-900/40">
                    <span className="text-sm font-medium flex-1">Devolución</span>
                    <Badge variant="muted">{r.status}</Badge>
                    {r.refundAmount !== null && (
                      <span className="font-mono text-sm">{formatMoney(r.refundAmount, currency)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}

function KpiCard({ label, value, hint, hintColor }: { label: string; value: string; hint?: string; hintColor?: string }) {
  return (
    <Card className="p-5">
      <p className="text-xs text-neutral-400 uppercase tracking-wider mb-2">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      {hint && <p className={cn('text-xs mt-1', hintColor ?? 'text-neutral-500')}>{hint}</p>}
    </Card>
  )
}

function InfoItem({ icon: Icon, label, value, mono }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="size-4 text-neutral-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-neutral-500">{label}</p>
        <p className={cn('text-sm', mono && 'font-mono')}>{value}</p>
      </div>
    </div>
  )
}

function orderStatusColor(s: OrderRow['status']) {
  switch (s) {
    case 'pending': return 'text-yellow-400'
    case 'processing': return 'text-orange-400'
    case 'shipped': return 'text-blue-400'
    case 'delivered': return 'text-emerald-400'
    case 'cancelled': return 'text-red-400'
  }
}

function paymentStatusColor(s: OrderRow['paymentStatus']) {
  switch (s) {
    case 'paid': return 'text-emerald-400'
    case 'pending': return 'text-yellow-400'
    case 'failed': return 'text-red-400'
    case 'refunded': return 'text-neutral-400'
  }
}

function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(amount / 100)
}
